import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { PageHeader } from '../components/PageHeader';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ROLE_LABELS, formatDate } from '../lib/format';
import { useToast } from '../hooks/use-toast';

const initialForm = { erp_user_id: '', role: 'requestor', company_name: '', department: '', is_active: true };
const initials = (n) => (n || '?').split(' ').map((s) => s[0]).slice(0, 2).join('');

const ErpUserPicker = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(async () => {
      try { setRows((await api.get('/users/erp-candidates', { params: { q } })).data); } catch { setRows([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input className="pl-8" placeholder="Search ERP users by name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="esd-user-search" /></div>
      <div className="max-h-48 overflow-y-auto border rounded-md divide-y" data-testid="esd-user-candidates">
        {rows.length === 0 && <div className="p-3 text-xs text-slate-500">No ERP users without ESD access match.</div>}
        {rows.map((r) => (
          <button type="button" key={r.id} onClick={() => onChange(r)} data-testid={`esd-user-candidate-${r.id}`}
            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${value === r.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
            <div className="h-7 w-7 rounded-full bg-[hsl(217,91%,24%)] text-white grid place-items-center text-[10px] font-bold">{initials(r.full_name)}</div>
            <div className="min-w-0"><div className="font-medium truncate">{r.full_name}</div><div className="text-xs text-slate-500 truncate">{r.email}</div></div>
          </button>
        ))}
      </div>
    </div>
  );
};

const Users = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [picked, setPicked] = useState(null);
  const [editing, setEditing] = useState(null);
  const isSuperAdmin = hasRole(user, 'super_admin');

  const load = useCallback(async () => {
    setUsers((await api.get('/users')).data);
    try { setRoles((await api.get('/roles')).data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleLabel = (key) => roles.find((r) => r.key === key)?.label || ROLE_LABELS[key] || key;
  const err = (e) => toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' });

  const save = async () => {
    try {
      if (editing && editing.is_member) {
        const { role, company_name, department, is_active } = form;
        await api.put(`/users/${editing.id}`, { role, company_name, department, is_active });
        toast({ title: 'Updated' });
      } else if (editing) {
        await api.post('/users', { erp_user_id: editing.id, role: form.role || 'requestor', company_name: form.company_name, department: form.department, is_active: form.is_active });
        toast({ title: 'ESD access granted' });
      } else {
        if (!form.erp_user_id) { toast({ title: 'Pick an ERP user first', variant: 'destructive' }); return; }
        await api.post('/users', form);
        toast({ title: 'User added to ESD' });
      }
      setOpen(false); load();
    } catch (e) { err(e); }
  };
  const remove = async (u) => { if (!window.confirm(`Remove ESD access for ${u.full_name}?`)) return; try { await api.delete(`/users/${u.id}`); load(); } catch (e) { err(e); } };

  return (
    <div className="space-y-4">
      <PageHeader title="User Management" description="ERP users with access to the Equipment Solutions Department and their ESD role."
        actions={<Button data-testid="esd-add-user-btn" onClick={() => { setEditing(null); setPicked(null); setForm(initialForm); setOpen(true); }} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" />Add User</Button>} />
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="esd-users-table">
          <thead className="bg-slate-50 border-b text-left text-slate-600"><tr><th className="p-3">User</th><th className="p-3">Role</th><th className="p-3">Company / Department</th><th className="p-3">Status</th><th className="p-3">Added</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y">
            {users.map((u) => (
              <tr key={u.id} className="table-row-hover" data-testid={`esd-user-row-${u.id}`}>
                <td className="p-3"><div className="flex items-center gap-2"><div className="h-8 w-8 rounded-full bg-[hsl(217,91%,24%)] text-white grid place-items-center text-xs font-bold">{initials(u.full_name)}</div><div><div className="font-medium">{u.full_name}</div><div className="text-xs text-slate-500 font-mono">{u.email || u.username}</div></div></div></td>
                <td className="p-3">{u.is_member ? <Badge variant="secondary" className="bg-blue-100 text-blue-800 hover:bg-blue-100">{roleLabel(u.role)}</Badge> : <Badge variant="outline" className="text-slate-500">No access</Badge>}</td>
                <td className="p-3 text-xs">{u.company_name}<div className="text-slate-500">{u.department}</div></td>
                <td className="p-3">{!u.is_member ? <span className="text-xs text-slate-400">—</span> : u.is_active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</td>
                <td className="p-3 text-xs">{formatDate(u.created_at)}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`esd-user-edit-${u.id}`} onClick={() => { setEditing(u); setForm({ ...initialForm, ...u, role: u.role || 'requestor', is_active: u.is_member ? u.is_active : true }); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button>
                  {isSuperAdmin && u.is_member && u.id !== user.id && <Button size="sm" variant="ghost" data-testid={`esd-user-delete-${u.id}`} onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.full_name}` : 'Add User'}</DialogTitle><DialogDescription>{editing ? 'Update this user’s ESD role and status.' : 'Pick an existing ERP user and assign an ESD role. They sign in with their ERP account.'}</DialogDescription></DialogHeader>
          {!editing && <ErpUserPicker value={form.erp_user_id} onChange={(r) => { setPicked(r); setForm({ ...form, erp_user_id: r.id, department: form.department || r.department || '' }); }} />}
          {!editing && picked && <div className="text-xs text-slate-600" data-testid="esd-user-picked">Selected: <strong>{picked.full_name}</strong></div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="esd-user-role-select"><SelectValue /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}{!r.is_builtin && <span className="ml-1 text-[10px] text-slate-400">(custom)</span>}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="flex items-end"><label className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} data-testid="esd-user-active-switch" /> <span className="text-sm">Active</span></label></div>
            <div><Label>Company</Label><Input value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="esd-user-company" /></div>
            <div><Label>Department</Label><Input value={form.department || ''} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="esd-user-department" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="esd-user-save-btn" className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Add'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
